// StopwatchScreen.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { ScreenOrientation } from "@capacitor/screen-orientation";
import { KeepAwake } from "@capacitor-community/keep-awake";

import {
    Box,
    Button,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import ScreenRotationRoundedIcon from "@mui/icons-material/ScreenRotationRounded";

const COLORS = {
    background: "#000000",
    text: "#f5f5f5",
    surface: "#151515",
    muted: "#8a8a8a",
    border: "#3a3a3a",
    indicatorInitial: "#ffffff",
    indicatorGreen: "#35c759",
    indicatorYellow: "#ffd60a",
    indicatorRed: "#ff3b30",
    indicatorFinal: "#b02656",
};

const StopwatchScreen = () => {
    const screenRef = useRef(null);
    const startedAtRef = useRef(null);
    const savedElapsedRef = useRef(0);

    const [elapsed, setElapsed] = useState(0);
    const [isRunning, setIsRunning] = useState(false);

    const start = useCallback(() => {
        if (startedAtRef.current !== null) {
            return;
        }

        startedAtRef.current = performance.now();
        setIsRunning(true);
    }, []);

    const pause = () => {
        if (startedAtRef.current === null) {
            return;
        }

        savedElapsedRef.current += performance.now() - startedAtRef.current;
        startedAtRef.current = null;

        setElapsed(savedElapsedRef.current);
        setIsRunning(false);
    };

    const reset = () => {
        startedAtRef.current = null;
        savedElapsedRef.current = 0;

        setElapsed(0);
        setIsRunning(false);
    };

    useEffect(() => {
        if (!Capacitor.isNativePlatform()) return undefined;

        KeepAwake.keepAwake().catch((error) =>
            console.warn("Keep awake is not available:", error),
        );

        return () => {
            KeepAwake.allowSleep().catch((error) =>
                console.warn("Could not allow screen sleep:", error),
            );
        };
    }, []);

    useEffect(() => {
        if (!isRunning) {
            return;
        }

        let animationFrame;

        const update = () => {
            if (startedAtRef.current === null) {
                return;
            }

            setElapsed(
                savedElapsedRef.current +
                    performance.now() -
                    startedAtRef.current,
            );

            animationFrame = window.requestAnimationFrame(update);
        };

        animationFrame = window.requestAnimationFrame(update);

        return () => window.cancelAnimationFrame(animationFrame);
    }, [isRunning]);

    useEffect(() => {
        const handleVolumeUp = (event) => {
            if (
                event.repeat ||
                ![event.key, event.code, String(event.keyCode)].some((value) =>
                    ["AudioVolumeUp", "VolumeUp", "175", "24"].includes(value),
                )
            ) {
                return;
            }

            event.preventDefault();
            start();
        };

        window.addEventListener("keydown", handleVolumeUp, true);

        // Native Android wrappers can dispatch this event.
        window.addEventListener("stopwatch-volume-up", start);

        return () => {
            window.removeEventListener("keydown", handleVolumeUp, true);
            window.removeEventListener("stopwatch-volume-up", start);
        };
    }, [start]);

    const centiseconds = Math.floor(elapsed / 10);
    // StopwatchScreen.jsx
    const time = `${String(Math.floor(centiseconds / 6000)).padStart(2, "0")}:${String(Math.floor((centiseconds % 6000) / 100)).padStart(2, "0")}`;
    const hundredths = String(centiseconds % 100).padStart(2, "0");

    return (
        <Box
            ref={screenRef}
            sx={{
                position: "relative",
                boxSizing: "border-box",
                width: "100%",
                minHeight: "100dvh",
                p: { xs: 2, sm: 4 },
                overflow: "hidden",
                display: "grid",
                placeItems: "center",
                bgcolor: COLORS.background,
                color: COLORS.text,
                userSelect: "none",
                "&:fullscreen": {
                    width: "100vw",
                    height: "100dvh",
                    bgcolor: COLORS.background,
                },
            }}
        >
            <Tooltip title="Girar pantalla">
                <IconButton
                    aria-label="Girar pantalla"
                    onClick={async () => {
                        try {
                            if (Capacitor.isNativePlatform()) {
                                const { type } =
                                    await ScreenOrientation.orientation();

                                await ScreenOrientation.lock({
                                    orientation: type.startsWith("portrait")
                                        ? "landscape"
                                        : "portrait",
                                });

                                return;
                            }

                            const orientation =
                                window.screen.orientation?.type?.startsWith(
                                    "portrait",
                                )
                                    ? "landscape"
                                    : "portrait";

                            if (!document.fullscreenElement) {
                                await screenRef.current?.requestFullscreen?.();
                            }

                            await window.screen.orientation?.lock?.(
                                orientation,
                            );
                        } catch (error) {
                            console.warn(
                                "Screen rotation is not available:",
                                error,
                            );
                        }
                    }}
                    sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        color: COLORS.text,
                        bgcolor: COLORS.surface,
                        border: `1px solid ${COLORS.border}`,
                        "&:hover": {
                            bgcolor: COLORS.border,
                        },
                    }}
                >
                    <ScreenRotationRoundedIcon />
                </IconButton>
            </Tooltip>

            <Stack
                spacing={{ xs: 5, sm: 7 }}
                alignItems="center"
                sx={{ width: "100%" }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "baseline",
                        justifyContent: "center",
                        whiteSpace: "nowrap",
                        fontFamily:
                            '"Roboto Mono", "SFMono-Regular", Consolas, monospace',
                        fontVariantNumeric: "tabular-nums",
                        lineHeight: 1,
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            font: "inherit",
                            fontSize: "clamp(80px, 20vw, 300px)",
                            fontWeight: 300,
                            letterSpacing: "-0.06em",
                        }}
                    >
                        {time}
                    </Typography>

                    <Typography
                        component="span"
                        sx={{
                            font: "inherit",
                            ml: "0.12em",
                            fontSize: "clamp(25px, 5vw, 78px)",
                            color: COLORS.muted,
                        }}
                    >
                        .{hundredths}
                    </Typography>
                </Box>

                <Stack
                    direction="row"
                    spacing={{ xs: 1, sm: 2 }}
                    alignItems="center"
                >
                    <Button
                        variant="contained"
                        startIcon={
                            isRunning ? (
                                <PauseRoundedIcon />
                            ) : (
                                <PlayArrowRoundedIcon />
                            )
                        }
                        onClick={isRunning ? pause : start}
                        sx={{
                            minWidth: "clamp(105px, 24vw, 190px)",
                            px: 3,
                            py: 1.25,
                            borderRadius: 10,
                            bgcolor: COLORS.text,
                            color: COLORS.background,
                            fontSize: "clamp(15px, 2vw, 18px)",
                            fontWeight: 700,
                            textTransform: "none",
                            "&:hover": {
                                bgcolor: COLORS.muted,
                            },
                        }}
                    >
                        {isRunning ? "Pausar" : "Iniciar"}
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={<RestartAltRoundedIcon />}
                        disabled={elapsed === 0}
                        onClick={reset}
                        sx={{
                            minWidth: "clamp(105px, 24vw, 190px)",
                            px: 3,
                            py: 1.25,
                            borderRadius: 10,
                            borderColor: COLORS.border,
                            color: COLORS.text,
                            fontSize: "clamp(15px, 2vw, 18px)",
                            fontWeight: 700,
                            textTransform: "none",
                            "&:hover": {
                                borderColor: COLORS.text,
                                bgcolor: COLORS.surface,
                            },
                            "&.Mui-disabled": {
                                borderColor: COLORS.surface,
                                color: COLORS.muted,
                            },
                        }}
                    >
                        Reiniciar
                    </Button>
                    <Box
                        aria-label="Indicador de tiempo"
                        sx={{
                            width: "clamp(54px, 8vw, 82px)",
                            aspectRatio: "1",
                            flexShrink: 0,
                            borderRadius: "50%",
                            bgcolor:
                                elapsed >= 10 * 60 * 1000
                                    ? COLORS.indicatorFinal
                                    : elapsed >= 9 * 60 * 1000
                                      ? COLORS.indicatorRed
                                      : elapsed >= 7 * 60 * 1000
                                        ? COLORS.indicatorYellow
                                        : elapsed >= 5 * 60 * 1000
                                          ? COLORS.indicatorGreen
                                          : COLORS.indicatorInitial,
                            animation:
                                elapsed >= 9 * 60 * 1000 + 30 * 1000 &&
                                elapsed < 10 * 60 * 1000
                                    ? "stopwatchIndicatorBlink 0.2s linear infinite"
                                    : elapsed >= 8 * 60 * 1000 &&
                                        elapsed < 10 * 60 * 1000
                                      ? "stopwatchIndicatorBlink 1s linear infinite"
                                      : "none",
                            "@keyframes stopwatchIndicatorBlink": {
                                "0%, 49%": { opacity: 1 },
                                "50%, 100%": { opacity: 0.12 },
                            },
                        }}
                    />
                </Stack>
            </Stack>
        </Box>
    );
};

export default StopwatchScreen;
